import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';

interface LogEntry {
  id: string;
  timestamp: string;
  level: number;
  levelName: string;
  message: string;
  context?: {
    component?: string;
    operation?: string;
    target?: string;
    duration?: number;
    count?: number;
    emoji?: string;
    [key: string]: unknown;
  };
  error?: {
    message: string;
    stack?: string;
    name: string;
  };
}

const LOG_LEVELS = {
  0: { name: 'DEBUG', color: 'text-gray-500', bg: 'bg-gray-100 dark:bg-gray-700' },
  1: { name: 'INFO', color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-100 dark:bg-blue-900/40' },
  2: { name: 'WARN', color: 'text-yellow-600 dark:text-yellow-400', bg: 'bg-yellow-100 dark:bg-yellow-900/40' },
  3: { name: 'ERROR', color: 'text-red-600 dark:text-red-400', bg: 'bg-red-100 dark:bg-red-900/40' }
};

export default function LogsPage() {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<LogEntry[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLevel, setSelectedLevel] = useState<number | null>(null);
  const [selectedComponent, setSelectedComponent] = useState<string>('');
  const [autoScroll, setAutoScroll] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());
  const [jsonView, setJsonView] = useState<Set<string>>(new Set());
  const [stats, setStats] = useState({
    total: 0,
    byLevel: { DEBUG: 0, INFO: 0, WARN: 0, ERROR: 0 }
  });

  const logsContainerRef = useRef<HTMLDivElement>(null);
  const eventSourceRef = useRef<EventSource | null>(null);

  const toggleLogExpansion = (logId: string) => {
    setExpandedLogs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const toggleJsonView = (logId: string) => {
    setJsonView(prev => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const getComponents = () => {
    const components = new Set<string>();
    logs.forEach(log => {
      if (log.context?.component) {
        components.add(log.context.component);
      }
    });
    return Array.from(components).sort();
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }) + '.' + date.getMilliseconds().toString().padStart(3, '0');
  };

  const formatDuration = (duration?: number) => {
    if (!duration) return '';
    if (duration < 1000) return `${duration}ms`;
    return `${(duration / 1000).toFixed(1)}s`;
  };

  const connectToSSE = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const token = localStorage.getItem('token');
    if (!token) return;

    const eventSource = new EventSource(`/api/logs/stream?token=${encodeURIComponent(token)}`);
    eventSourceRef.current = eventSource;

    eventSource.onopen = () => {
      setIsConnected(true);
    };

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.type === 'connected') {
          console.log('Connected to log stream');
          return;
        }
        
        if (data.type === 'clear') {
          setLogs([]);
          setFilteredLogs([]);
          // Reset stats when logs are cleared
          setStats({
            total: 0,
            byLevel: { DEBUG: 0, INFO: 0, WARN: 0, ERROR: 0 }
          });
          return;
        }

        // Regular log entry
        if (data.id) {
          setLogs(prevLogs => {
            const newLogs = [...prevLogs, data];
            
            // Keep only last 1000 logs to prevent memory issues
            const finalLogs = newLogs.slice(-1000);
            
            // Update stats based on actual logs in memory
            setStats({
              total: finalLogs.length,
              byLevel: finalLogs.reduce((acc, log) => {
                const levelName = log.levelName as keyof typeof acc;
                if (levelName in acc) {
                  acc[levelName]++;
                }
                return acc;
              }, { DEBUG: 0, INFO: 0, WARN: 0, ERROR: 0 })
            });
            
            return finalLogs;
          });
        }
      } catch (error) {
        console.error('Error parsing SSE data:', error);
      }
    };

    eventSource.onerror = () => {
      setIsConnected(false);
      setTimeout(() => {
        if (eventSourceRef.current?.readyState === EventSource.CLOSED) {
          connectToSSE();
        }
      }, 5000);
    };
  }, []);

  const disconnectFromSSE = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const fetchLogStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/logs/stats', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const stats = await response.json();
        setStats(stats);
      }
    } catch (error) {
      console.error('Error fetching log stats:', error);
    }
  }, []);

  const clearLogs = async () => {
    if (!window.confirm('Are you sure you want to clear all logs? This action cannot be undone.')) {
      return;
    }

    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/logs', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        setLogs([]);
        setFilteredLogs([]);
        // Reset stats immediately after clearing
        setStats({
          total: 0,
          byLevel: { DEBUG: 0, INFO: 0, WARN: 0, ERROR: 0 }
        });
      } else {
        console.error('Failed to clear logs');
      }
    } catch (error) {
      console.error('Error clearing logs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const exportLogs = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const levelParam = selectedLevel !== null ? `?level=${selectedLevel}` : '';
      
      const response = await fetch(`/api/logs/export${levelParam}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `dashboard-logs-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error('Failed to export logs');
      }
    } catch (error) {
      console.error('Error exporting logs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter logs based on search and filters
  useEffect(() => {
    let filtered = logs;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(log =>
        log.message.toLowerCase().includes(query) ||
        (log.context?.component && log.context.component.toLowerCase().includes(query)) ||
        (log.context?.operation && log.context.operation.toLowerCase().includes(query))
      );
    }

    if (selectedLevel !== null) {
      filtered = filtered.filter(log => log.level >= selectedLevel);
    }

    if (selectedComponent) {
      filtered = filtered.filter(log => log.context?.component === selectedComponent);
    }

    setFilteredLogs(filtered);
  }, [logs, searchQuery, selectedLevel, selectedComponent]);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (autoScroll && logsContainerRef.current) {
      logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
    }
  }, [filteredLogs, autoScroll]);

  // Connect to SSE on mount and fetch stats
  useEffect(() => {
    connectToSSE();
    fetchLogStats();

    return () => {
      disconnectFromSSE();
    };
  }, [connectToSSE, fetchLogStats, disconnectFromSSE]);

  const components = getComponents();

  return (
    <div className="px-4">
      {/* Breadcrumb Navigation */}
      <nav className="flex mb-4" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-3">
          <li className="inline-flex items-center">
            <Link
              to="/admin/management"
              className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              Management
            </Link>
          </li>
          <li>
            <div className="flex items-center">
              <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
              <span className="ml-1 md:ml-2 text-sm font-medium text-gray-500 dark:text-gray-400">System Logs</span>
            </div>
          </li>
        </ol>
      </nav>

      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">System Logs</h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              Real-time backend logging and system monitoring
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <span className="text-sm text-gray-600 dark:text-gray-300">
                {isConnected ? 'Connected' : 'Disconnected'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow dark:border dark:border-gray-700">
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.total}</div>
          <div className="text-sm text-gray-600 dark:text-gray-300">Total Logs</div>
        </div>
        {Object.entries(stats.byLevel).map(([level, count]) => {
          const levelNum = ['DEBUG', 'INFO', 'WARN', 'ERROR'].indexOf(level);
          const config = LOG_LEVELS[levelNum as keyof typeof LOG_LEVELS];
          return (
            <div key={level} className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow dark:border dark:border-gray-700">
              <div className={`text-2xl font-bold ${config.color}`}>{count}</div>
              <div className="text-sm text-gray-600 dark:text-gray-300">{level}</div>
            </div>
          );
        })}
      </div>

      {/* Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6 dark:border dark:border-gray-700">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search logs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          {/* Level Filter */}
          <div>
            <select
              value={selectedLevel?.toString() || ''}
              onChange={(e) => setSelectedLevel(e.target.value ? parseInt(e.target.value) : null)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">All Levels</option>
              <option value="0">DEBUG+</option>
              <option value="1">INFO+</option>
              <option value="2">WARN+</option>
              <option value="3">ERROR</option>
            </select>
          </div>

          {/* Component Filter */}
          <div>
            <select
              value={selectedComponent}
              onChange={(e) => setSelectedComponent(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">All Components</option>
              {components.map(component => (
                <option key={component} value={component}>{component}</option>
              ))}
            </select>
          </div>

          {/* Auto-scroll Toggle */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="autoScroll"
              checked={autoScroll}
              onChange={(e) => setAutoScroll(e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600"
            />
            <label htmlFor="autoScroll" className="text-sm text-gray-700 dark:text-gray-300">
              Auto-scroll
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-2">
            <button
              onClick={clearLogs}
              disabled={isLoading}
              className="px-3 py-2 bg-red-600 dark:bg-red-500 text-white text-sm font-medium rounded-md hover:bg-red-700 dark:hover:bg-red-600 disabled:opacity-50"
            >
              Clear
            </button>
            <button
              onClick={exportLogs}
              disabled={isLoading}
              className="px-3 py-2 bg-green-600 dark:bg-green-500 text-white text-sm font-medium rounded-md hover:bg-green-700 dark:hover:bg-green-600 disabled:opacity-50"
            >
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Logs Display */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:border dark:border-gray-700">
        <div
          ref={logsContainerRef}
          className="h-96 overflow-y-auto p-4 font-mono text-sm"
        >
          {filteredLogs.length === 0 ? (
            <div className="text-center text-gray-500 dark:text-gray-400 py-8">
              {logs.length === 0 ? 'No logs available' : 'No logs match current filters'}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredLogs.map((log) => {
                const levelConfig = LOG_LEVELS[log.level as keyof typeof LOG_LEVELS];
                const isExpanded = expandedLogs.has(log.id);
                const showJson = jsonView.has(log.id);
                
                // Filter out basic context fields for detailed view
                const detailedContext = log.context ? Object.fromEntries(
                  Object.entries(log.context).filter(([key]) => 
                    !['component', 'operation', 'target', 'duration', 'count', 'emoji'].includes(key)
                  )
                ) : {};
                
                const hasDetailedContext = Object.keys(detailedContext).length > 0;
                const hasExpandableContent = hasDetailedContext || log.error || 
                  (log.context && (log.context.operation || log.context.target || log.context.count !== undefined));
                
                return (
                  <div
                    key={log.id}
                    className={`p-3 rounded text-xs ${levelConfig.bg} border-l-4 border-l-transparent hover:border-l-gray-400 dark:hover:border-l-gray-500`}
                  >
                    {/* Main log line */}
                    <div className="flex items-start justify-between">
                      <div className="flex items-center space-x-2 flex-1 min-w-0">
                        {hasExpandableContent && (
                          <button
                            onClick={() => toggleLogExpansion(log.id)}
                            className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-transform duration-200"
                            style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
                          >
                            ▶
                          </button>
                        )}
                        <span className="text-gray-500 dark:text-gray-400 flex-shrink-0">
                          {formatTimestamp(log.timestamp)}
                        </span>
                        <span className={`font-medium flex-shrink-0 ${levelConfig.color}`}>
                          [{log.levelName}]
                        </span>
                        {log.context?.component && (
                          <span className="text-purple-600 dark:text-purple-400 flex-shrink-0">
                            {log.context.component}
                          </span>
                        )}
                        {log.context?.emoji && (
                          <span className="flex-shrink-0">{log.context.emoji}</span>
                        )}
                        <span className="text-gray-900 dark:text-gray-100 break-words">
                          {log.message}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                        {log.context?.duration && (
                          <span className="text-gray-500 dark:text-gray-400 text-xs">
                            {formatDuration(log.context.duration)}
                          </span>
                        )}
                        {hasExpandableContent && isExpanded && (
                          <button
                            onClick={() => toggleJsonView(log.id)}
                            className={`px-2 py-1 text-xs rounded ${
                              showJson 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                            } hover:opacity-80`}
                          >
                            JSON
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {/* Expanded details */}
                    {isExpanded && (
                      <div className="mt-3 pl-4 border-l-2 border-gray-300 dark:border-gray-600">
                        {showJson ? (
                          /* JSON View */
                          <div className="bg-gray-50 dark:bg-gray-900 rounded p-3">
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">Raw Log Data</div>
                            <pre className="text-xs text-gray-800 dark:text-gray-200 whitespace-pre-wrap overflow-x-auto">
                              {JSON.stringify(log, null, 2)}
                            </pre>
                          </div>
                        ) : (
                          /* Formatted View */
                          <div className="space-y-3">
                            {/* Basic Context */}
                            {(log.context?.operation || log.context?.target || log.context?.count !== undefined) && (
                              <div className="bg-gray-50 dark:bg-gray-900/50 rounded p-2">
                                <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Context</div>
                                <div className="space-y-1">
                                  {log.context.operation && (
                                    <div className="text-xs text-gray-600 dark:text-gray-400">
                                      <span className="font-medium">Operation:</span> {log.context.operation}
                                    </div>
                                  )}
                                  {log.context.target && (
                                    <div className="text-xs text-gray-600 dark:text-gray-400">
                                      <span className="font-medium">Target:</span> {log.context.target}
                                    </div>
                                  )}
                                  {log.context.count !== undefined && (
                                    <div className="text-xs text-gray-600 dark:text-gray-400">
                                      <span className="font-medium">Count:</span> {log.context.count}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Additional Context Data */}
                            {hasDetailedContext && (
                              <div className="bg-blue-50 dark:bg-blue-900/20 rounded p-2">
                                <div className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">Additional Data</div>
                                <div className="space-y-1">
                                  {Object.entries(detailedContext).map(([key, value]) => (
                                    <div key={key} className="text-xs text-blue-600 dark:text-blue-400">
                                      <span className="font-medium">{key}:</span> {
                                        typeof value === 'object' 
                                          ? JSON.stringify(value) 
                                          : String(value)
                                      }
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Error Details */}
                            {log.error && (
                              <div className="bg-red-50 dark:bg-red-900/20 rounded p-2 border border-red-200 dark:border-red-800">
                                <div className="text-xs font-medium text-red-700 dark:text-red-300 mb-2">Error Details</div>
                                <div className="space-y-2">
                                  <div className="text-xs text-red-800 dark:text-red-300">
                                    <span className="font-medium">Type:</span> {log.error.name}
                                  </div>
                                  <div className="text-xs text-red-800 dark:text-red-300">
                                    <span className="font-medium">Message:</span> {log.error.message}
                                  </div>
                                  {log.error.stack && (
                                    <div>
                                      <div className="text-xs font-medium text-red-700 dark:text-red-300 mb-1">Stack Trace:</div>
                                      <pre className="text-xs text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-950 p-2 rounded overflow-x-auto">
                                        {log.error.stack}
                                      </pre>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}